import { apiClient } from '../api-client'

export interface ReportFilters {
  project_id?: string
  assignee_id?: string
  assignee_ids?: string[]
  team_id?: string
  status_filter?: string
  status_filters?: string[]
  work_type?: string
  range_preset?: string
  date_from?: string
  date_to?: string
  limit?: number
}

export interface WorkReportRow {
  id: string
  title: string
  project_id: string
  project_name: string
  status: string
  priority?: string
  type?: string
  assignee_id?: string | null
  assignee_name: string
  assigned_date?: string | null
  completed_date?: string | null
  assigned_hours: number
  utilized_hours: number
  assigned_by_id?: string | null
  assigned_by_name?: string | null
  completed_by_id?: string | null
  completed_by_name?: string | null
  due_date?: string | null
}

export interface WorkReportResponse {
  data: WorkReportRow[]
  total: number
  limit: number
}

export interface ResourcePlanningBucket {
  date: string
  label: string
  capacity_hours: number
  allocated_hours: number
  utilized_hours: number
  occupancy_percent: number
  status: 'free' | 'available' | 'busy' | 'overloaded'
  is_weekend: boolean
  items: Array<ResourcePlanningItem & { allocated_hours: number }>
}

export interface ResourcePlanningItem {
  id: string
  title: string
  work_type: 'task' | 'bug'
  project_id: string
  project_name: string
  status: string
  priority?: string | null
  assigned_hours: number
  utilized_hours: number
  remaining_hours: number
  start_date?: string | null
  end_date?: string | null
  due_date?: string | null
  unscheduled: boolean
}

export interface ResourcePlanningResource {
  user_id: string
  user_name: string
  role: string
  total_capacity_hours: number
  total_allocated_hours: number
  total_utilized_hours: number
  total_remaining_hours: number
  available_hours: number
  occupancy_percent: number
  overloaded: boolean
  unscheduled_hours: number
  buckets: ResourcePlanningBucket[]
  items: ResourcePlanningItem[]
}

export interface ResourcePlanningResponse {
  range: {
    start_date: string
    end_date: string
    days: Array<{
      date: string
      label: string
      weekday: string
      is_weekend: boolean
    }>
  }
  summary: {
    resource_count: number
    total_capacity_hours: number
    total_allocated_hours: number
    total_available_hours: number
    overloaded_count: number
    unscheduled_hours: number
  }
  resources: ResourcePlanningResource[]
}

function cleanFilters(filters?: ReportFilters): ReportFilters {
  const cleaned: ReportFilters = {}
  const writableFilters = cleaned as Record<string, unknown>

  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '' || value === 'all') return
    if (Array.isArray(value)) {
      if (value.length === 0) return
      writableFilters[key] = value.join(',')
      return
    }
    writableFilters[key] = value
  })

  return cleaned
}

export const reportsApi = {
  getTasksReport: (filters?: ReportFilters): Promise<WorkReportResponse> =>
    apiClient.get('/reports/tasks', { params: cleanFilters(filters) }),

  getBugsReport: (filters?: ReportFilters): Promise<WorkReportResponse> =>
    apiClient.get('/reports/bugs', { params: cleanFilters(filters) }),

  getResourcePlanningReport: (filters?: ReportFilters): Promise<ResourcePlanningResponse> =>
    apiClient.get('/reports/resource-planning', { params: cleanFilters(filters) }),
}
